/**
 * HelpdeskPage.jsx · v2.7.45+E16 (Phase 6 · Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Three-pane support-ticket console.
 *
 *   [ Tabs ]  [ Ticket list ]   [ Ticket detail · comments · history ]
 *
 * Tabs: My Tickets · Active · All · Closed
 * Filters: source · priority · search
 *
 * Access:
 *   • supervisor / dispatcher / operating_partner / developer / admin → full
 *   • drivers → read-only · only tickets where claimed_by == self
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, LifeBuoy, Plus, Filter, RefreshCw, Loader2, MessageCircle,
  CheckCircle2, Clock, AlertTriangle, X, Send, UserCheck, Users, ChevronRight,
  Mail, Phone, MapPin, ArrowRightLeft, Search, Tag, TimerReset, Flame,
} from "lucide-react";

// v2.7.65+E96 · Escalation SLA thresholds (mirror of ESCALATION_HOURS in backend)
const ESCALATION_HOURS = { urgent: 6, high: 12 };
const ESCALATION_DEFAULT_HOURS = 24;

function _slaFor(ticket) {
  const created = ticket?.created_at ? new Date(ticket.created_at).getTime() : 0;
  if (!created) return null;
  const priority = (ticket?.priority || "normal").toLowerCase();
  const thresholdH = ESCALATION_HOURS[priority] ?? ESCALATION_DEFAULT_HOURS;
  const dueMs = created + thresholdH * 3600 * 1000;
  const now = Date.now();
  const remainingMs = dueMs - now;
  return {
    priority,
    thresholdHours: thresholdH,
    escalated: !!ticket?.escalated_at,
    dueMs,
    remainingMs,
    remainingLabel: _humanDeltaShort(remainingMs),
  };
}

function _humanDeltaShort(ms) {
  const past = ms < 0;
  const abs = Math.abs(ms);
  const mins = Math.floor(abs / 60000);
  if (mins < 60) return `${past ? "-" : ""}${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) return `${past ? "-" : ""}${hrs}h ${remMins}m`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return `${past ? "-" : ""}${days}d ${remHrs}h`;
}

const STATUS_META = {
  open:               { label: "Open",        cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",  Icon: Clock },
  in_progress:        { label: "In Progress", cls: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200",          Icon: ArrowRightLeft },
  waiting_customer:   { label: "Awaiting",    cls: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200", Icon: Loader2 },
  resolved:           { label: "Resolved",    cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200", Icon: CheckCircle2 },
  closed:             { label: "Closed",      cls: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300", Icon: X },
};
const PRIORITY_META = {
  low:    { label: "Low",    cls: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400" },
  normal: { label: "Normal", cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300" },
  high:   { label: "High",   cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200" },
  urgent: { label: "Urgent", cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200" },
};
const SOURCE_META = {
  chat:   { Icon: MessageCircle, label: "Chat" },
  email:  { Icon: Mail,          label: "Email" },
  manual: { Icon: Phone,         label: "Manual" },
};
const OWNER_ROLES = [
  { value: "supervisor",        label: "Supervisor" },
  { value: "dispatcher",        label: "Dispatch" },
  { value: "operating_partner", label: "Operating Partner" },
  { value: "developer",         label: "Developer" },
  { value: "admin",             label: "Admin" },
];

export default function HelpdeskPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "active");
  const [filters, setFilters] = useState({ source: "all", priority: "all", q: "" });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(params.get("ticket") || null);
  const [showNew, setShowNew] = useState(false);
  const [showEscalation, setShowEscalation] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (tab === "mine") q.set("mine", "true");
      else if (tab === "active") q.set("status", "open"); // load both open + in_progress below
      else if (tab === "closed") q.set("status", "closed");
      else q.set("status", "all");
      if (filters.source !== "all") q.set("source", filters.source);
      if (filters.priority !== "all") q.set("priority", filters.priority);
      if (filters.q.trim()) q.set("q", filters.q.trim());
      const [r1, r2] = await Promise.all([
        adminApi.get(`/admin/tickets?${q.toString()}`),
        // For "active" we also fetch in_progress
        tab === "active"
          ? adminApi.get(`/admin/tickets?status=in_progress${filters.source !== "all" ? `&source=${filters.source}` : ""}${filters.priority !== "all" ? `&priority=${filters.priority}` : ""}${filters.q.trim() ? `&q=${encodeURIComponent(filters.q.trim())}` : ""}`)
          : Promise.resolve({ data: { items: [] } }),
      ]);
      const merged = [...(r1.data?.items || []), ...(r2.data?.items || [])];
      // Dedup by id and sort newest first
      const seen = new Set();
      const dedup = merged.filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)));
      dedup.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setTickets(dedup);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [tab, filters]);

  useEffect(() => { load(); }, [load]);

  // Keep query string in sync so deep-links to a ticket work
  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    if (activeId) next.set("ticket", activeId); else next.delete("ticket");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activeId]);

  const filteredCount = tickets.length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 flex flex-col" data-testid="helpdesk-page">
      {/* Header */}
      <header className="bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="helpdesk-back" className="gap-1.5">
            <ArrowLeft className="h-4 w-4"/>Admin Console
          </Button>
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-emerald-600"/>
            <h1 className="text-lg font-extrabold">Helpdesk · Support Tickets</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowEscalation(true)}
            variant="outline"
            size="sm"
            className="gap-1.5 h-8 text-xs border-amber-500/60 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/20"
            data-testid="helpdesk-escalation-btn"
          >
            <TimerReset className="h-3.5 w-3.5"/>Escalation
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 h-8 text-xs" data-testid="helpdesk-new-ticket-btn">
            <Plus className="h-3.5 w-3.5"/>New Ticket
          </Button>
          <Button onClick={load} variant="outline" size="sm" className="gap-1.5 h-8 text-xs" disabled={loading} data-testid="helpdesk-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}Refresh
          </Button>
        </div>
      </header>

      {/* Tabs + filters */}
      <div className="bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 px-5 flex items-center gap-1 overflow-x-auto">
        {[
          { id: "active", label: "Active",   testid: "helpdesk-tab-active" },
          { id: "mine",   label: "My Tickets", testid: "helpdesk-tab-mine" },
          { id: "all",    label: "All",      testid: "helpdesk-tab-all" },
          { id: "closed", label: "Closed",   testid: "helpdesk-tab-closed" },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold border-b-2 transition-colors whitespace-nowrap
              ${tab === t.id
                ? "border-emerald-600 text-emerald-700 dark:text-emerald-300"
                : "border-transparent text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-zinc-100"}`}
            data-testid={t.testid}
          >
            {t.label}
            <span className="text-[10px] uppercase tracking-widest font-bold opacity-60">
              {tab === t.id ? `(${filteredCount})` : ""}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-slate-100 dark:bg-zinc-900/40 border-b border-slate-200 dark:border-zinc-800 px-5 py-2 flex items-center gap-2 flex-wrap">
        <Filter className="h-3.5 w-3.5 text-slate-500"/>
        <select
          value={filters.source}
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}
          className="h-7 text-xs border border-slate-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-950 px-2"
          data-testid="helpdesk-filter-source"
        >
          <option value="all">All sources</option>
          <option value="chat">Chat</option>
          <option value="email">Email</option>
          <option value="manual">Manual</option>
        </select>
        <select
          value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          className="h-7 text-xs border border-slate-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-950 px-2"
          data-testid="helpdesk-filter-priority"
        >
          <option value="all">All priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400"/>
          <Input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search subject, body, ticket-no…"
            className="pl-7 h-7 text-xs"
            data-testid="helpdesk-search"
          />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Ticket list */}
        <div className="w-96 border-r border-slate-200 dark:border-zinc-800 overflow-y-auto bg-white dark:bg-zinc-950">
          {loading && tickets.length === 0 && (
            <div className="text-center py-10 text-xs text-slate-500 italic"><Loader2 className="h-4 w-4 mx-auto mb-1 animate-spin"/>Loading tickets…</div>
          )}
          {!loading && tickets.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-500 dark:text-zinc-500 italic px-4" data-testid="helpdesk-empty">
              No tickets match this filter.
            </div>
          )}
          {tickets.map((t) => {
            const SourceIcon = SOURCE_META[t.source]?.Icon;
            const sla = _slaFor(t);
            const isActiveStatus = ["open", "in_progress", "waiting_customer"].includes(t.status);
            const showSla = isActiveStatus && sla;
            const slaColor = !showSla
              ? ""
              : sla.escalated
                ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200"
                : sla.remainingMs < 0
                  ? "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200 animate-pulse"
                  : sla.remainingMs < 3600000
                    ? "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200"
                    : sla.remainingMs < 6 * 3600000
                      ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                      : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200";
            return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveId(t.id)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-zinc-900 hover:bg-slate-50 dark:hover:bg-zinc-900/50 transition-colors ${activeId === t.id ? "bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-l-emerald-600" : ""}`}
              data-testid={`helpdesk-ticket-row-${t.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-[10px] font-bold text-slate-500">{t.ticket_no}</span>
                {SourceIcon && <SourceIcon className="h-3 w-3 text-slate-400"/>}
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded uppercase tracking-widest font-bold ${PRIORITY_META[t.priority]?.cls || ""}`}>
                  {t.priority}
                </span>
              </div>
              <div className="font-bold text-sm truncate">{t.subject}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-widest font-bold ${STATUS_META[t.status]?.cls || ""}`}>
                  {STATUS_META[t.status]?.label || t.status}
                </span>
                {showSla && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-bold ${slaColor}`}
                    data-testid={`helpdesk-sla-badge-${t.id}`}
                    title={sla.escalated
                      ? "Already escalated to dispatch@ + legal@"
                      : sla.remainingMs < 0
                        ? `Past SLA by ${sla.remainingLabel.replace("-", "")} · escalates on next sweep`
                        : `Escalates in ${sla.remainingLabel} (SLA ${sla.thresholdHours}h)`}
                  >
                    {sla.escalated ? (<><Flame className="h-2.5 w-2.5"/>Escalated</>)
                      : sla.remainingMs < 0 ? (<><Flame className="h-2.5 w-2.5"/>Overdue · {sla.remainingLabel.replace("-", "")}</>)
                      : (<><TimerReset className="h-2.5 w-2.5"/>{sla.remainingLabel}</>)}
                  </span>
                )}
                <span className="text-[10px] text-slate-500 truncate">{t.customer_name || "—"}</span>
              </div>
            </button>
          );})}
        </div>

        {/* Detail */}
        <div className="flex-1 overflow-y-auto">
          {activeId
            ? <TicketDetail key={activeId} ticketId={activeId} onChange={load} onClose={() => setActiveId(null)}/>
            : <div className="flex-1 flex flex-col items-center justify-center h-full text-slate-400 dark:text-zinc-600">
                <LifeBuoy className="h-12 w-12 mb-2 opacity-50"/>
                <div className="font-bold">Select a ticket</div>
                <div className="text-xs">Pick one on the left to see comments & history.</div>
              </div>
          }
        </div>
      </div>

      {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={(t) => { setShowNew(false); load(); setActiveId(t.id); }}/>}
      {showEscalation && <EscalationSweepModal onClose={() => setShowEscalation(false)} onSwept={() => { setShowEscalation(false); load(); }}/>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Detail pane
════════════════════════════════════════════════════════════════════ */
function TicketDetail({ ticketId, onChange, onClose }) {
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [showAssign, setShowAssign] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/tickets/${ticketId}`);
      setT(r.data);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!comment.trim()) return;
    try {
      await adminApi.post(`/admin/tickets/${ticketId}/comment`, { body: comment, internal: false });
      setComment("");
      load();
      onChange?.();
    } catch (e) { toast.error(`Post failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const claim = async () => {
    try {
      await adminApi.post(`/admin/tickets/${ticketId}/claim`);
      toast.success("Ticket claimed");
      load(); onChange?.();
    } catch (e) { toast.error(`Claim failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const transition = async (status) => {
    try {
      await adminApi.post(`/admin/tickets/${ticketId}/transition`, { status });
      toast.success(`Marked ${status.replace(/_/g, " ")}`);
      load(); onChange?.();
    } catch (e) { toast.error(`Transition failed: ${e?.response?.data?.detail || e.message}`); }
  };

  if (loading || !t) {
    return <div className="flex items-center justify-center h-full text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2"/>Loading ticket…</div>;
  }

  const StatusIcon = STATUS_META[t.status]?.Icon || Clock;

  return (
    <div className="flex flex-col h-full" data-testid={`ticket-detail-${t.id}`}>
      <div className="border-b border-slate-200 dark:border-zinc-800 p-5 bg-white dark:bg-zinc-950">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold text-slate-500">{t.ticket_no}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-widest font-bold ${PRIORITY_META[t.priority]?.cls || ""}`}>{t.priority}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-widest font-bold ${STATUS_META[t.status]?.cls || ""} flex items-center gap-1`}>
                <StatusIcon className="h-3 w-3"/>{STATUS_META[t.status]?.label || t.status}
              </span>
              {t.topic_tag && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-zinc-800 font-bold uppercase tracking-widest flex items-center gap-1"><Tag className="h-3 w-3"/>{t.topic_tag}</span>}
            </div>
            <h2 className="text-xl font-bold" data-testid="ticket-subject">{t.subject}</h2>
            <div className="text-xs text-slate-500 mt-1">
              {t.customer_name || "—"}{t.customer_email ? ` · ${t.customer_email}` : ""}{t.customer_phone ? ` · ${t.customer_phone}` : ""}
              {" · "}created {t.created_at?.slice(0, 16).replace("T", " ")}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0" aria-label="Close"><X className="h-4 w-4"/></Button>
        </div>
        {t.body && (
          <div className="mt-3 p-3 bg-slate-50 dark:bg-zinc-900/40 rounded text-sm whitespace-pre-wrap">{t.body}</div>
        )}
        {/* Action bar */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {/* v2.7.65+E109 · Ticket ↔ Chat deep-link. Shows only when the
              ticket has a linked chat session. Opens StaffChatPanel with
              the target session in a fresh tab so the ticket stays open. */}
          {(() => {
            const chatSid = t.chat_session_id || t.source_ref?.chat_session_id;
            if (!chatSid) return null;
            return (
              <Button
                onClick={() => window.open(`/admin/chat?session=${encodeURIComponent(chatSid)}`, "_blank")}
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5 border-emerald-500/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                data-testid="ticket-open-chat-transcript"
                title={`Open chat transcript for session ${chatSid.slice(0, 8)}…`}
              >
                <MessageCircle className="h-3 w-3"/>Open chat transcript
              </Button>
            );
          })()}
          <Button onClick={claim} size="sm" variant="outline" className="h-7 text-xs gap-1.5" data-testid="ticket-claim-btn">
            <UserCheck className="h-3 w-3"/>Claim
          </Button>
          <Button onClick={() => setShowAssign(true)} size="sm" variant="outline" className="h-7 text-xs gap-1.5" data-testid="ticket-assign-btn">
            <Users className="h-3 w-3"/>Assign…
          </Button>
          {t.status !== "in_progress" && t.status !== "closed" && (
            <Button onClick={() => transition("in_progress")} size="sm" variant="outline" className="h-7 text-xs gap-1.5" data-testid="ticket-mark-progress">
              <ArrowRightLeft className="h-3 w-3"/>In Progress
            </Button>
          )}
          {t.status !== "resolved" && (
            <Button onClick={() => transition("resolved")} size="sm" variant="outline" className="h-7 text-xs gap-1.5 text-emerald-700" data-testid="ticket-mark-resolved">
              <CheckCircle2 className="h-3 w-3"/>Resolve
            </Button>
          )}
          {t.status !== "closed" && (
            <Button onClick={() => transition("closed")} size="sm" variant="outline" className="h-7 text-xs gap-1.5" data-testid="ticket-mark-closed">
              <X className="h-3 w-3"/>Close
            </Button>
          )}
        </div>
      </div>

      {/* Comments + history */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <section data-testid="ticket-comments">
          <h3 className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Comments ({t.comments?.length || 0})</h3>
          {(!t.comments || t.comments.length === 0) && <div className="text-xs text-slate-500 italic">No comments yet.</div>}
          {(t.comments || []).map((c) => (
            <div key={c.id} className="p-3 bg-slate-50 dark:bg-zinc-900/40 rounded mb-2">
              <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                <span className="font-bold">{c.by}{c.internal ? " · internal" : ""}</span>
                <span>{c.at?.slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="text-sm whitespace-pre-wrap">{c.body}</div>
            </div>
          ))}
        </section>
        <section data-testid="ticket-history">
          <h3 className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">History</h3>
          <div className="space-y-1">
            {(t.history || []).slice().reverse().map((h) => (
              <div key={`${h.at}-${h.kind}-${h.by}`} className="text-[11px] text-slate-500 flex items-center gap-2">
                <ChevronRight className="h-3 w-3"/>
                <span className="font-mono">{h.at?.slice(0, 16).replace("T", " ")}</span>
                <span className="font-bold">{h.by}</span>
                <span>· {h.kind}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Reply box */}
      <div className="border-t border-slate-200 dark:border-zinc-800 p-4 bg-white dark:bg-zinc-950">
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment…"
          rows={2}
          className="text-sm"
          data-testid="ticket-comment-input"
        />
        <div className="flex justify-end mt-2">
          <Button onClick={post} disabled={!comment.trim()} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 h-8 text-xs" data-testid="ticket-comment-submit">
            <Send className="h-3.5 w-3.5"/>Post
          </Button>
        </div>
      </div>

      {showAssign && <AssignModal ticketId={ticketId} onClose={() => setShowAssign(false)} onDone={() => { setShowAssign(false); load(); onChange?.(); }}/>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Assign modal — role OR specific person (mirrors chat handover modal)
════════════════════════════════════════════════════════════════════ */
function AssignModal({ ticketId, onClose, onDone }) {
  const [mode, setMode] = useState("role"); // 'role' | 'user'
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    if (mode !== "user") return;
    adminApi.get("/admin/chat/staff-online").then((r) => setStaff(r.data?.items || [])).catch(() => {});
  }, [mode]);

  const assign = async (kind, target) => {
    try {
      await adminApi.post(`/admin/tickets/${ticketId}/assign`, { owner_kind: kind, owner_target: target });
      toast.success(`Assigned to ${target.replace(/_/g, " ")}`);
      onDone();
    } catch (e) { toast.error(`Assign failed: ${e?.response?.data?.detail || e.message}`); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="ticket-assign-modal">
      <div className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-emerald-600"/>
          <h3 className="font-bold text-lg">Assign ticket</h3>
        </div>
        <div className="flex gap-1 mb-3 bg-slate-100 dark:bg-zinc-900 rounded p-1">
          {[
            { id: "role", label: "By Role" },
            { id: "user", label: "Specific Person" },
          ].map((m) => (
            <button key={m.id} type="button" onClick={() => setMode(m.id)}
              className={`flex-1 px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded transition-colors
                ${mode === m.id ? "bg-white dark:bg-zinc-800 text-emerald-700 dark:text-emerald-300 shadow" : "text-slate-600 dark:text-zinc-400"}`}
              data-testid={`ticket-assign-tab-${m.id}`}>
              {m.label}
            </button>
          ))}
        </div>
        {mode === "role" ? (
          <div className="space-y-2">
            {OWNER_ROLES.map((r) => (
              <button key={r.value} type="button" onClick={() => assign("role", r.value)}
                className="w-full text-left p-3 border border-slate-200 dark:border-zinc-800 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors flex items-center justify-between"
                data-testid={`ticket-assign-role-${r.value}`}>
                <span className="font-bold text-sm">{r.label}</span>
                <ChevronRight className="h-3.5 w-3.5 text-slate-400"/>
              </button>
            ))}
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1" data-testid="ticket-assign-user-list">
            {staff.length === 0 && <div className="text-center py-6 text-xs text-slate-500 italic"><Loader2 className="h-4 w-4 mx-auto mb-1 animate-spin"/>Loading staff…</div>}
            {staff.map((u) => (
              <button key={u.id} type="button" onClick={() => assign("user", u.id)}
                className="w-full text-left p-2.5 border border-slate-200 dark:border-zinc-800 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2"
                data-testid={`ticket-assign-user-${u.id}`}>
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${u.is_online ? "bg-emerald-500" : "bg-slate-300 dark:bg-zinc-600"}`}/>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-xs truncate">{u.name}</div>
                  <div className="text-[10px] text-slate-500 truncate uppercase tracking-widest">{(u.canonical_role || u.role || "").replace(/_/g, " ")}</div>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-slate-400"/>
              </button>
            ))}
          </div>
        )}
        <Button variant="outline" onClick={onClose} className="w-full mt-3" data-testid="ticket-assign-cancel">Cancel</Button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   v2.7.65+E96 · Escalation Sweep Console
   ─────────────────────────────────────────────────────────────────────
   Shows every ticket currently past its priority-specific SLA (6h/12h/24h)
   and lets an admin trigger the escalation sweep on demand instead of
   waiting for the 15-min background scheduler.
════════════════════════════════════════════════════════════════════ */
function EscalationSweepModal({ onClose, onSwept }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/tickets/escalation/summary");
      setSummary(r.data);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await adminApi.post("/admin/tickets/escalation/sweep-now");
      const n = r.data?.escalated || 0;
      if (n > 0) toast.success(`Escalated ${n} ticket${n === 1 ? "" : "s"} · fanning out to dispatch@ + legal@`);
      else toast.info("Nothing to escalate — all tickets within SLA");
      onSwept?.();
    } catch (e) {
      toast.error(`Sweep failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSweeping(false); }
  };

  const pending = summary?.pending || [];
  const policy = summary?.policy || {};
  const counts = summary?.counts || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}
         data-testid="escalation-sweep-modal">
      <div className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TimerReset className="h-5 w-5 text-amber-500"/>
            <div>
              <h3 className="font-bold text-lg">Ticket Escalation Queue</h3>
              <p className="text-[11px] text-slate-500 dark:text-zinc-500">
                Aged tickets fan out to their original alias + <code className="font-mono">dispatch@</code> + <code className="font-mono">legal@</code>. Sweep runs automatically every 15 min.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="escalation-sweep-close">
            <X className="h-4 w-4"/>
          </Button>
        </div>

        {/* SLA policy row */}
        <div className="px-5 py-3 border-b border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50">
          <div className="grid grid-cols-3 gap-3 text-center">
            <PolicyPill label="Urgent SLA"  hours={policy.urgent_hours}  danger />
            <PolicyPill label="High SLA"    hours={policy.high_hours}    warn />
            <PolicyPill label="Default SLA" hours={policy.default_hours} />
          </div>
        </div>

        {/* Counts + pending list */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-10 text-slate-500 text-sm italic">
              <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2"/>Loading escalation queue…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="escalation-counts">
                <CountPill label="Urgent"    n={counts.urgent    || 0} tone="rose" />
                <CountPill label="High"      n={counts.high      || 0} tone="amber" />
                <CountPill label="Normal"    n={counts.normal    || 0} tone="sky" />
                <CountPill label="Low"       n={counts.low       || 0} tone="zinc" />
                <CountPill label="Escalated" n={counts.escalated || 0} tone="emerald" />
              </div>

              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  {pending.length} ticket{pending.length === 1 ? "" : "s"} past SLA — ready to escalate
                </div>
                <Button
                  size="sm"
                  onClick={sweep}
                  disabled={sweeping || pending.length === 0}
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-xs h-8 gap-1.5"
                  data-testid="escalation-sweep-run"
                >
                  {sweeping ? <Loader2 className="h-3 w-3 animate-spin"/> : <Flame className="h-3 w-3"/>}
                  Sweep now ({pending.length})
                </Button>
              </div>

              {pending.length === 0 ? (
                <div className="text-center py-8 text-emerald-700 dark:text-emerald-300 text-sm font-bold"
                     data-testid="escalation-empty">
                  <CheckCircle2 className="h-6 w-6 mx-auto mb-2"/>
                  All tickets within SLA · nothing to escalate
                </div>
              ) : (
                <div className="border border-slate-200 dark:border-zinc-800 rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 dark:bg-zinc-900 text-slate-500 uppercase text-[10px] tracking-widest">
                      <tr>
                        <th className="text-left px-2 py-1.5">Ticket</th>
                        <th className="text-left px-2 py-1.5">Priority</th>
                        <th className="text-left px-2 py-1.5">Aged</th>
                        <th className="text-left px-2 py-1.5">SLA</th>
                        <th className="text-left px-2 py-1.5">Subject</th>
                      </tr>
                    </thead>
                    <tbody data-testid="escalation-pending-list">
                      {pending.map((t) => (
                        <tr key={t.id} className="border-t border-slate-200 dark:border-zinc-800"
                            data-testid={`escalation-pending-row-${t.id}`}>
                          <td className="px-2 py-1.5 font-mono text-[10px]">{t.ticket_no}</td>
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded uppercase text-[9px] font-bold ${PRIORITY_META[t.priority]?.cls || ""}`}>
                              {t.priority}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 tabular-nums font-bold text-rose-600 dark:text-rose-300">
                            {t.aged_hours ? `${t.aged_hours.toFixed(1)}h` : "—"}
                          </td>
                          <td className="px-2 py-1.5 tabular-nums text-slate-500">
                            {t.threshold_hours ? `${t.threshold_hours.toFixed(0)}h` : "—"}
                          </td>
                          <td className="px-2 py-1.5 truncate max-w-[180px]" title={t.subject}>{t.subject}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-zinc-800 flex items-center justify-between bg-slate-50 dark:bg-zinc-900/50">
          <div className="text-[10px] text-slate-500">
            <AlertTriangle className="h-3 w-3 inline mr-1"/>
            Each ticket only escalates once — sweeping again is safe (already-escalated tickets are skipped).
          </div>
          <Button variant="ghost" size="sm" onClick={load} className="h-7 text-xs" data-testid="escalation-refresh">
            <RefreshCw className="h-3 w-3 mr-1"/>Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}

function PolicyPill({ label, hours, danger, warn }) {
  const cls = danger
    ? "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200"
    : warn
      ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
      : "bg-slate-200 text-slate-700 dark:bg-zinc-800 dark:text-zinc-300";
  return (
    <div className={`rounded px-2 py-1.5 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="font-bold text-sm tabular-nums">{hours ?? "?"}h</div>
    </div>
  );
}

function CountPill({ label, n, tone = "zinc" }) {
  const map = {
    rose:    "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200",
    amber:   "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",
    sky:     "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200",
    zinc:    "bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-300",
    emerald: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200",
  };
  return (
    <div className={`rounded p-2 text-center ${map[tone] || map.zinc}`} data-testid={`escalation-count-${label.toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="font-bold text-lg tabular-nums">{n}</div>
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════
   New ticket modal — manual creation (phone-in calls etc.)
════════════════════════════════════════════════════════════════════ */
function NewTicketModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    subject: "", body: "", priority: "normal",
    customer_name: "", customer_phone: "", customer_email: "", topic_tag: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.subject.trim()) { toast.error("Subject required"); return; }
    setSaving(true);
    try {
      const r = await adminApi.post("/admin/tickets", { ...form, source: "manual" });
      toast.success(`Created ${r.data?.ticket_no}`);
      onCreated(r.data);
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="helpdesk-new-modal">
      <div className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Plus className="h-5 w-5 text-emerald-600"/>
          <h3 className="font-bold text-lg">New ticket · phone-in / manual</h3>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Subject *</label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="text-sm mt-1" data-testid="new-ticket-subject"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Customer name</label>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="text-sm mt-1" data-testid="new-ticket-customer-name"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Phone</label>
              <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="text-sm mt-1" data-testid="new-ticket-phone"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full h-9 border border-slate-300 dark:border-zinc-700 rounded text-sm mt-1 px-2 bg-white dark:bg-zinc-950" data-testid="new-ticket-priority">
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Topic tag (optional)</label>
              <Input value={form.topic_tag} onChange={(e) => setForm({ ...form, topic_tag: e.target.value })} placeholder="billing, lost_item, complaint…" className="text-sm mt-1" data-testid="new-ticket-topic"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Body</label>
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} className="text-sm mt-1" data-testid="new-ticket-body"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="new-ticket-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="new-ticket-create">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Plus className="h-4 w-4 mr-1"/>}Create
          </Button>
        </div>
      </div>
    </div>
  );
}
